/**
 * Equipment data access for the admin SPA.
 *
 * Talks to the Go service via the shared api-client. Mirrors the JSON shape of
 * the legacy Next.js endpoint at web/src/app/api/admin/equipment/route.ts.
 *
 * The Go service implements the full CRUD surface: `GET` (list), `POST`
 * (create), `PATCH` (autosave edit), and `DELETE ?id=` (remove) on
 * `/api/admin/equipment` — see go-service/internal/api/equipment.go (#108).
 */
import { apiFetch } from "../../../shared/api-client";
import type { WarehouseStockEntry } from "./warehouses-api";

/** Normalised equipment item used throughout the page. */
export interface EquipmentItem {
  id: string;
  name: string;
  hire_rate_per_day: number;
  stock_quantity: number;
  // Hired-in gear (PR2). is_hired_in marks an item as not owned stock;
  // supplier_ids are the suppliers that can source it.
  is_hired_in: boolean;
  allow_double_book: boolean;
  // Physical facts (#189). null = unknown, never zero. volume_m3 is the
  // DB's generated column — null unless all three dimensions are set.
  weight_kg: number | null;
  length_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
  insurable_value: number | null;
  volume_m3: number | null;
  /** Free-text warehouse location (#190). null = not recorded. */
  storage_location: string | null;
  supplier_ids: string[];
  /**
   * Per-warehouse stock split (#304). The shelf (`storage_location`) says
   * where on a site; this says which site. `stock_quantity` above is the
   * DB-derived sum of these rows, so the two can never disagree.
   */
  warehouse_stock: WarehouseStockEntry[];
}

/** Fields editable via on-blur autosave. */
export type EquipmentField = "name" | "hire_rate_per_day" | "stock_quantity";
export type EquipmentPatch = Partial<Pick<EquipmentItem, EquipmentField>>;

/** One {profile_key, role_key, sort_order} chain slot sent by the edit modal. */
export interface ChainAssignment {
  profile_key: string;
  role_key: string;
  sort_order: number;
}

/**
 * One role_orders entry: the ordered other-member equipment ids for a role the
 * operator actually reordered (the edited item excluded — its own position
 * rides in chain_assignments[].sort_order).
 */
export interface RoleOrderEntry {
  profile_key: string;
  role_key: string;
  equipment_ids: string[];
}

/**
 * Client-side composite key for React state keyed by role — role_key is only
 * unique per (company, profile). ':' cannot appear in either key (DB check:
 * ^[a-z0-9][a-z0-9_]*$), and this key is NEVER sent to the server.
 */
export function roleId(r: { profile_key: string; role_key: string }): string {
  return `${r.profile_key}:${r.role_key}`;
}

/**
 * The full equipment edit payload (modal "Save"). Carries the scalar fields
 * (name/rate/qty — same set as the "Add new item" form) alongside the
 * hired-in relations: is_hired_in toggles ownership; supplier_ids replaces the
 * linked set; chain_assignments is the COMPLETE list of role memberships for
 * this item (a role omitted here removes the item from that role). When
 * supplier_ids or chain_assignments is present the Go handler writes the whole
 * lot — scalar row + supplier diff + role reorder — in one transaction.
 *
 * role_orders persists the edit form's reordering of a role's OTHER members —
 * one RoleOrderEntry per role the operator actually reordered; the Go handler
 * can only REORDER existing members through it (never add/remove one), and it
 * must ride alongside chain_assignments.
 */
export interface EquipmentRelationsPatch {
  name?: string;
  hire_rate_per_day?: number;
  stock_quantity?: number;
  // Physical facts: explicit null clears back to unknown; an absent key
  // leaves the column untouched.
  weight_kg?: number | null;
  length_mm?: number | null;
  width_mm?: number | null;
  height_mm?: number | null;
  insurable_value?: number | null;
  // Warehouse location: explicit null (or "") clears it back to not recorded.
  storage_location?: string | null;
  is_hired_in?: boolean;
  supplier_ids?: string[];
  chain_assignments?: ChainAssignment[];
  role_orders?: RoleOrderEntry[];
}

/**
 * One gear role with its ordered member ladder (GET /api/admin/gear-roles).
 * Roles come from the company's enabled inquiry profiles; role_key is only
 * unique per profile, so consumers key state by roleId(role).
 */
export interface GearRole {
  profile_key: string;
  profile_label: string;
  role_key: string;
  display_name: string;
  members: { equipment_id: string; name: string; sort_order: number }[];
}

/**
 * Raw item shape as returned by the Go service. `hire_rate_per_day` arrives as
 * a numeric string; `stock_quantity` is nullable.
 */
interface RawEquipmentItem {
  id: unknown;
  name: unknown;
  hire_rate_per_day?: unknown;
  stock_quantity?: unknown;
}

function isRawItem(value: unknown): value is RawEquipmentItem {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as RawEquipmentItem).id === "string" &&
    typeof (value as RawEquipmentItem).name === "string"
  );
}

/** null unless the value is a finite number — unknown never becomes zero. */
function finiteOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Coerce one raw row into a normalised EquipmentItem. */
function normalise(raw: RawEquipmentItem): EquipmentItem {
  const rate = Number(raw.hire_rate_per_day);
  const r = raw as RawEquipmentItem & {
    is_hired_in?: unknown;
    allow_double_book?: unknown;
    weight_kg?: unknown;
    length_mm?: unknown;
    width_mm?: unknown;
    height_mm?: unknown;
    insurable_value?: unknown;
    volume_m3?: unknown;
    storage_location?: unknown;
    supplier_ids?: unknown;
    warehouse_stock?: unknown;
  };
  return {
    id: raw.id as string,
    name: raw.name as string,
    hire_rate_per_day: Number.isFinite(rate) ? rate : 0,
    stock_quantity:
      typeof raw.stock_quantity === "number" ? raw.stock_quantity : 0,
    is_hired_in: r.is_hired_in === true,
    allow_double_book: r.allow_double_book === true,
    weight_kg: finiteOrNull(r.weight_kg),
    length_mm: finiteOrNull(r.length_mm),
    width_mm: finiteOrNull(r.width_mm),
    height_mm: finiteOrNull(r.height_mm),
    insurable_value: finiteOrNull(r.insurable_value),
    volume_m3: finiteOrNull(r.volume_m3),
    storage_location:
      typeof r.storage_location === "string" && r.storage_location !== ""
        ? r.storage_location
        : null,
    supplier_ids: Array.isArray(r.supplier_ids)
      ? r.supplier_ids.filter((s): s is string => typeof s === "string")
      : [],
    warehouse_stock: normaliseWarehouseStock(r.warehouse_stock),
  };
}

/**
 * Coerce the per-warehouse split (#304). A malformed entry is dropped rather
 * than defaulted: a warehouse quantity that silently reads 0 is a number an
 * Operator would act on, and a wrong stock number is the bug this feature
 * exists to fix.
 */
function normaliseWarehouseStock(v: unknown): WarehouseStockEntry[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const e = entry as Record<string, unknown>;
    if (typeof e.warehouse_id !== "string" || e.warehouse_id === "") return [];
    if (typeof e.quantity !== "number" || !Number.isFinite(e.quantity)) return [];
    return [
      {
        warehouse_id: e.warehouse_id,
        warehouse_name:
          typeof e.warehouse_name === "string" ? e.warehouse_name : "",
        quantity: e.quantity,
      },
    ];
  });
}

/**
 * Fetch the equipment list, optionally narrowed to one Warehouse (#304).
 * `warehouseId` blank/undefined is the whole catalogue — the only shape a
 * single-site Company ever asks for.
 */
export async function listEquipment(
  signal?: AbortSignal,
  warehouseId?: string,
): Promise<EquipmentItem[]> {
  const data = await apiFetch<unknown>("/api/admin/equipment", {
    signal,
    query: warehouseId ? { warehouse: warehouseId } : undefined,
  });
  if (!Array.isArray(data)) throw new Error("Expected equipment array");
  return data.filter(isRawItem).map(normalise);
}

/**
 * Fetch one equipment row for the detail page (#333). Targets
 * `GET /api/admin/equipment/{id}` — a path param, unlike the `?id=` writes.
 * The Go handler returns the same row shape as the list, so it normalises
 * through the same coercion.
 */
export async function getEquipment(
  id: string,
  signal?: AbortSignal,
): Promise<EquipmentItem> {
  const data = await apiFetch<unknown>(`/api/admin/equipment/${id}`, {
    signal,
  });
  if (!isRawItem(data)) throw new Error("Expected an equipment item");
  return normalise(data);
}

/**
 * Persist an autosave edit. Targets `PATCH /api/admin/equipment`. Body matches
 * the Go handler (and the legacy Next.js handler): `{ id, ...patch }`.
 */
export async function patchEquipment(
  id: string,
  patch: EquipmentPatch,
): Promise<void> {
  await apiFetch<void>("/api/admin/equipment", {
    method: "PATCH",
    body: { id, ...patch },
  });
}

/**
 * Persist the hired-in edit modal's combined payload. Targets the same
 * `PATCH /api/admin/equipment`; the Go handler routes supplier/chain writes
 * through one transaction. Always sends arrays explicitly so an empty array
 * clears the corresponding set.
 */
export async function patchEquipmentRelations(
  id: string,
  patch: EquipmentRelationsPatch,
): Promise<void> {
  await apiFetch<void>("/api/admin/equipment", {
    method: "PATCH",
    body: { id, ...patch },
  });
}

/** Fetch all gear roles + their ordered member ladders. */
export async function listGearRoles(signal?: AbortSignal): Promise<GearRole[]> {
  const res = await apiFetch<{ roles?: GearRole[] }>("/api/admin/gear-roles", {
    signal,
  });
  return (res.roles ?? []).map((role) => ({
    ...role,
    profile_key: typeof role.profile_key === "string" ? role.profile_key : "",
    profile_label:
      typeof role.profile_label === "string" ? role.profile_label : "",
    members: Array.isArray(role.members) ? role.members : [],
  }));
}

/**
 * Create a role in the catalogue (#320). role_key derives server-side from
 * the display name; profile_key may be omitted when the company runs one
 * enabled profile (the Go handler resolves it). Returns the created role.
 */
export async function createGearRole(
  displayName: string,
  profileKey?: string,
): Promise<GearRole> {
  const res = await apiFetch<{ role?: GearRole }>("/api/admin/gear-roles", {
    method: "POST",
    body: profileKey
      ? { display_name: displayName, profile_key: profileKey }
      : { display_name: displayName },
  });
  if (!res?.role) throw new Error("Expected a role");
  return { ...res.role, members: [] };
}

/** Rename a role — display_name only; role_key never changes. */
export async function renameGearRole(
  profileKey: string,
  roleKey: string,
  displayName: string,
): Promise<void> {
  await apiFetch<void>("/api/admin/gear-roles", {
    method: "PATCH",
    body: {
      profile_key: profileKey,
      role_key: roleKey,
      display_name: displayName,
    },
  });
}

/**
 * Delete a role. The Go handler refuses (409) while equipment or a Demand
 * Rule still references it.
 */
export async function deleteGearRole(
  profileKey: string,
  roleKey: string,
): Promise<void> {
  await apiFetch<void>("/api/admin/gear-roles", {
    method: "DELETE",
    query: { profile_key: profileKey, role_key: roleKey },
  });
}

/** Fields accepted when creating a new equipment row. */
export interface EquipmentCreate {
  name: string;
  hire_rate_per_day?: number;
  stock_quantity?: number;
  weight_kg?: number | null;
  length_mm?: number | null;
  width_mm?: number | null;
  height_mm?: number | null;
  insurable_value?: number | null;
  storage_location?: string | null;
  // Optional relations — when present the Go handler commits the row + flags +
  // supplier diff + role reorder in ONE transaction, so a relation-write
  // failure rolls the row back and never orphans it (#307). Omit them for a
  // plain scalar create. role_orders has the same shape + semantics as on
  // EquipmentRelationsPatch (other-member reorder per touched role).
  is_hired_in?: boolean;
  supplier_ids?: string[];
  chain_assignments?: ChainAssignment[];
  role_orders?: RoleOrderEntry[];
}

/**
 * Create a new equipment row. Targets `POST /api/admin/equipment`. The Go
 * handler applies defaults for omitted scalar fields (rate 0, qty 1). When the input also carries relations (is_hired_in / supplier_ids /
 * chain_assignments) the create is atomic — row and relations
 * commit together or not at all — so the caller never has to follow up with a
 * separate patchEquipmentRelations on the fresh id. Returns the new row's id,
 * or null if the response omits one.
 */
export async function createEquipment(
  input: EquipmentCreate,
): Promise<string | null> {
  const res = await apiFetch<{ id?: unknown }>("/api/admin/equipment", {
    method: "POST",
    body: input,
  });
  return typeof res?.id === "string" ? res.id : null;
}

/**
 * Delete an equipment row. Targets `DELETE /api/admin/equipment?id=<uuid>` —
 * the id rides in the query string, matching the Go handler.
 */
export async function deleteEquipment(id: string): Promise<void> {
  await apiFetch<void>("/api/admin/equipment", {
    method: "DELETE",
    query: { id },
  });
}
